import {
  IMAGE_LOADER,
  ImageLoaderConfig,
  registerLocaleData,
} from '@angular/common';
import {
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import localDe from '@angular/common/locales/de';
import localDeExtra from '@angular/common/locales/extra/de';
import {
  ApplicationConfig,
  importProvidersFrom,
  LOCALE_ID,
} from '@angular/core';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
} from '@angular/router';
import { provideAuth0 } from '@auth0/auth0-angular';
import { globalErrorHandlerProviders } from '@core/handler';
import { appInitializerProviders } from '@core/initializers';
import { httpInterceptorProviders } from '@core/interceptor';
import { environment } from '@env/environment';
import { graphqlProviders } from '@graphql/graphql-api.config';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { PaginatorI18nService } from '@shared/services';
import { NgxPermissionsModule } from 'ngx-permissions';
import { NgProgressRouterModule } from 'ngx-progressbar/router';
import { routes } from './app.routes';
import { WINDOW } from '@core/injection-tokens';
import 'moment/locale/de';
import { provideMomentDatetimeAdapter } from '@ng-matero/extensions-moment-adapter';
import { resolveLocale } from '@shared/utils';

/* Register de locale */
registerLocaleData(localDe, 'de-DE', localDeExtra);

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimationsAsync(),
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),
      withComponentInputBinding()
    ),
    provideHttpClient(withInterceptorsFromDi()),
    provideAuth0({
      domain: environment.AUTH0_DOMAIN,
      clientId: environment.AUTH0_CLIENT_ID,
      authorizationParams: {
        redirect_uri: window.location.origin,
        audience: environment.AUTH0_GRAPHQL_AUDIENCE,
      },
      errorPath: 'error/auth',
      httpInterceptor: {
        allowedList: [
          {
            uri: environment.HTTP_GRAPHQL_ENDPOINT,
            allowAnonymous: true,
          },
        ],
      },
    }),
    provideMomentDateAdapter({
      parse: {
        dateInput: 'L',
      },
      display: {
        dateInput: 'L',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
      },
    }),
    provideMomentDatetimeAdapter({
      parse: {
        dateInput: 'L',
      },
      display: {
        dateInput: 'L',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
        monthInput: 'MMMM',
        timeInput: 'L, LT',
        datetimeInput: 'L, LT',
        popupHeaderDateLabel: 'MMMM DD, ddd',
      },
    }),
    importProvidersFrom(
      MatDialogModule,
      NgProgressRouterModule,
      NgxPermissionsModule.forRoot(),
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: (http: HttpClient) =>
            new TranslateHttpLoader(
              http,
              `${environment.ASSET_URL}/i18n/`,
              '.json'
            ),
          deps: [HttpClient],
        },
      })
    ),
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        floatLabel: 'always',
        subscriptSizing: 'dynamic',
      },
    },
    { provide: WINDOW, useValue: window },
    {
      provide: MAT_DATE_LOCALE,
      useFactory: () => resolveLocale(),
    },
    {
      provide: LOCALE_ID,
      useFactory: () => resolveLocale(),
    },
    {
      provide: MatPaginatorIntl,
      useFactory: (paginatorI18nService: PaginatorI18nService) =>
        paginatorI18nService.getPaginatorIntl(),
      deps: [PaginatorI18nService],
    },
    {
      provide: IMAGE_LOADER,
      useValue: (config: ImageLoaderConfig) => {
        return `${environment.ASSET_URL}/images/${config.src}`;
      },
    },
    graphqlProviders,
    appInitializerProviders,
    httpInterceptorProviders,
    globalErrorHandlerProviders,
  ],
};
